import React, { useState } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import Avatar from "shared/Avatar/Avatar";
import { 
  UserCircle, 
  Calendar, 
  Shield, 
  MessageCircle, 
  ChevronDown, 
  ChevronUp,
  Award,
  Star
} from "lucide-react";

interface HostInformationProps {
  propertyData: PropertyToEdit | null;
}

const HostInformation: React.FC<HostInformationProps> = ({ propertyData }) => {
  const [showFullBio, setShowFullBio] = useState(false);
  
  const createdAt = `${propertyData?.user?.created_at}`;
  const date = new Date(createdAt);

  const formattedDate = `Joined ${date.toLocaleString("en-US", {
    month: "long",
  })} ${date.getFullYear()}`;

  const hostName = propertyData 
    ? `${propertyData.user?.first_name} ${propertyData.user?.last_name}`
    : "";

  const aboutMe = propertyData?.user?.about_me || "";
  const shouldTruncate = aboutMe.length > 200;
  const truncatedBio = shouldTruncate
    ? aboutMe.substring(0, 200) + "..."
    : aboutMe;

  return (
    <div className="listingSection__wrap w-full">
      {/* Header Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl">
              <UserCircle size={24} className="text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Meet your host
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Hosted by {hostName.split(' ')[0] || 'Host'}
              </p>
            </div>
          </div>
          
          {/* Contact Link - Top Corner */}
          <button className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-500 dark:text-gray-400 hover:text-[#f5694b] dark:hover:text-[#f5694b] transition-colors">
            <MessageCircle size={14} className="sm:block hidden" />
            <span className="underline decoration-dotted underline-offset-2">Contact</span>
          </button>
        </div>
      </div>

      <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent my-6" />

      {/* Host Card */}
      <div className="p-6 bg-gradient-to-br from-white to-gray-50 dark:from-neutral-800 dark:to-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-700 shadow-sm">
        {/* Host Profile */}
        <div className="flex items-start gap-4 mb-6">
          {propertyData && (
            <div className="relative flex-shrink-0">
              <Avatar
                hasChecked
                sizeClass="h-20 w-20"
                radius="rounded-full"
                imgUrl={propertyData.user?.image}
              />
              {/* Verified Badge */}
              <div className="absolute -bottom-1 -right-1 p-1 bg-white dark:bg-neutral-800 rounded-full shadow-md">
                <Shield size={16} className="text-green-500" />
              </div>
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {hostName}
              </h3>
              <div className="flex items-center gap-1 px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 rounded-full">
                <Award size={12} className="text-indigo-600 dark:text-indigo-400" />
                <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                  Verified
                </span>
              </div>
            </div>

            {/* Host Stats */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-1.5">
                <Calendar size={16} />
                <span>{formattedDate}</span>
              </div>
              <div className="flex items-center gap-1">
                <Star size={16} className="text-yellow-500 fill-yellow-500" />
                <span className="font-medium text-gray-900 dark:text-white">
                  {propertyData?.average?.average_review?.toFixed(1) || "New"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Host Bio */}
        {aboutMe && (
          <div className="space-y-3">
            <div className="relative">
              <p className={`text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line ${
                !showFullBio && shouldTruncate ? "line-clamp-3" : ""
              }`}>
                {showFullBio ? aboutMe : truncatedBio}
              </p>
              
              {/* Gradient Overlay */}
              {!showFullBio && shouldTruncate && (
                <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-50 dark:from-neutral-900 to-transparent pointer-events-none" />
              )}
            </div>

            {shouldTruncate && (
              <button
                onClick={() => setShowFullBio(!showFullBio)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
              >
                {showFullBio ? (
                  <>
                    Show less
                    <ChevronUp size={16} />
                  </>
                ) : (
                  <>
                    Read more
                    <ChevronDown size={16} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Additional Info Cards */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Response Rate */}
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 mb-1">
            <MessageCircle size={16} className="text-green-600 dark:text-green-400" />
            <span className="text-sm font-medium text-green-900 dark:text-green-100">
              Response rate
            </span>
          </div>
          <p className="text-2xl font-bold text-green-700 dark:text-green-300">
            100%
          </p>
        </div>

        {/* Response Time */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-1">
            <Calendar size={16} className="text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Response time
            </span>
          </div>
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
            Within hours
          </p>
        </div>
      </div>

      {/* Protection Notice */}
      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
        <div className="flex gap-3">
          <Shield size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-900 dark:text-amber-100 font-medium mb-1">
              Your safety matters
            </p>
            <p className="text-xs text-amber-800 dark:text-amber-200">
              To protect your payment, never transfer money or communicate outside of the platform. 
              All hosts are verified for your safety.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostInformation;